import { useState, useEffect } from "react"
import { AppLayout } from "@/components/app-layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { Clock, Calendar } from "lucide-react"
import { dayOptions, hourOptions, minuteOptions, convertToCronExpression, parseExistingCron } from "@/lib/schedule-utils"

interface SettingsForm {
  day: string;
  hour: string;
  minute: string;
}

export default function Settings() {
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const { toast } = useToast()
  const form = useForm<SettingsForm>({
    defaultValues: {
      day: "*",
      hour: "9",
      minute: "0"
    }
  })

  useEffect(() => {
    const fetchCurrentSchedule = async () => {
      try {
        setIsInitializing(true)
        const { data, error } = await supabase.functions.invoke('get-cron-schedule')
        
        if (error) throw error
        
        if (data.schedule) {
          console.log("Retrieved schedule:", data.schedule)
          const { day, hour, minute } = parseExistingCron(data.schedule)
          console.log("Parsed values before form reset:", { day, hour, minute })
          console.log("Current form values:", form.getValues())
          form.reset({
            day,
            hour,
            minute
          })
          console.log("Form values after reset:", form.getValues())
        }
      } catch (error) {
        console.error("Error fetching schedule:", error)
        toast({
          title: "Erreur",
          description: "Impossible de récupérer la programmation actuelle.",
          variant: "destructive",
        })
      } finally {
        setIsInitializing(false)
      }
    }

    fetchCurrentSchedule()
  }, [form, toast])

  const onSubmit = async (data: SettingsForm) => {
    try {
      setIsLoading(true)
      const schedule = convertToCronExpression(data.day, data.hour, data.minute)
      console.log("Updating cron schedule to:", schedule)
      
      const { data: response, error } = await supabase.functions.invoke('update-cron-schedule', {
        body: { schedule }
      })

      if (error) throw error

      if (response?.schedule) {
        console.log("Schedule updated successfully:", response.schedule)
        toast({
          title: "Paramètres mis à jour",
          description: "La fréquence d'exécution a été modifiée avec succès.",
        })
      } else {
        throw new Error("No schedule returned from update")
      }
    } catch (error) {
      console.error("Error updating schedule:", error)
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour des paramètres.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AppLayout title="Paramètres">
      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Fréquence d'envoi des codes</CardTitle>
            <CardDescription>
              Configurez la fréquence à laquelle le système vérifie et envoie les codes programmés.
              Les heures sont dans le fuseau horaire de Cayenne (UTC-3).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="day"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Jour d'exécution</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                        disabled={isInitializing}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <Calendar className="mr-2 h-4 w-4" />
                            <SelectValue placeholder="Sélectionnez un jour" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {dayOptions.map((option) => (
                            <SelectItem 
                              key={option.value} 
                              value={option.value}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choisissez la fréquence d'exécution hebdomadaire
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="hour"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Heure</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                          disabled={isInitializing}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <Clock className="mr-2 h-4 w-4" />
                              <SelectValue placeholder="Sélectionnez une heure" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {hourOptions.map((option) => (
                              <SelectItem 
                                key={option.value} 
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="minute"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minute</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                          disabled={isInitializing}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <Clock className="mr-2 h-4 w-4" />
                              <SelectValue placeholder="Sélectionnez les minutes" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {minuteOptions.map((option) => (
                              <SelectItem 
                                key={option.value} 
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button type="submit" disabled={isLoading || isInitializing}>
                  {isLoading ? "Mise à jour..." : "Mettre à jour"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}